import { useCallback, useEffect, useState } from "react";

// Remembered widths for the time-entry grid's columns.
//
// A module-level store rather than plain component state, because Month and
// Custom mode stack one WeekView per week on a single page: dragging a column
// has to move that column in every grid at once, and localStorage alone cannot
// do that (the storage event does not fire in the tab that wrote it).
const KEY = "te_col_widths";

let widths = null;              // id -> px, as read from storage; null = unread
const listeners = new Set();

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    // Private windows, blocked site data, or junk left by an older build.
    return {};
  }
}

function write() {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(widths));
  } catch (_) {}
}

// Only columns the caller still knows about: a width saved for a column that
// has since been renamed or removed must not leak into the layout.
const applyTo = (defaults) => {
  const out = { ...defaults };
  Object.keys(defaults).forEach((id) => {
    if (typeof widths[id] === "number") out[id] = widths[id];
  });
  return out;
};

export function useColumnWidths(defaults) {
  if (widths === null) widths = read();
  const [, bump] = useState(0);

  useEffect(() => {
    const listener = () => bump((n) => n + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const setWidth = useCallback((id, px) => {
    widths = { ...widths, [id]: px };
    write();
    listeners.forEach((l) => l());
  }, []);

  return [applyTo(defaults), setWidth];
}

// Tests only: drop the in-memory copy so the next render re-reads storage,
// standing in for a fresh page load.
export function __resetColumnWidths() {
  widths = null;
  listeners.clear();
}
